import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from '../../utils/api';
import { showToast } from "../../utils/toast";

interface PlanItem {
    name: string;
    value: string;
    included: boolean;
    order: number;
}

const AddPlan = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [loading, setLoading] = useState(false);

    // Main Plan Data State
    const [formData, setFormData] = useState({
        plan_title: "",
        plan_subtext: "",
        plan_description: "",
        plan_mrp: "",
        plan_selling_price: "",
        plan_discount: "",
        plan_quarter_discount: "",
        plan_year_discount: "",
        plan_trial_days: "",
        plan_label: ""
    });


    // Dynamic Lists for Inclusions & Features
    const [inclusions, setInclusions] = useState<PlanItem[]>([{ name: "", value: "", included: true, order: 0 }]);
    const [features, setFeatures] = useState<PlanItem[]>([{ name: "", value: "", included: true, order: 0 }]);

    // --- FETCH DATA FOR EDIT MODE ---
    useEffect(() => {
        if (isEditMode && id) {
            fetchPlanData();
        }
    }, [id]);

    const moveRow = (index: number, direction: 'up' | 'down', type: 'inclusion' | 'feature') => {
        const list = type === 'inclusion' ? [...inclusions] : [...features];
        const newIndex = direction === 'up' ? index - 1 : index + 1;

        // Prevent moving out of bounds
        if (newIndex < 0 || newIndex >= list.length) return;

        // Swap items
        const [movedItem] = list.splice(index, 1);
        list.splice(newIndex, 0, movedItem);

        // Update state
        type === 'inclusion' ? setInclusions(list) : setFeatures(list);
    };

    const fetchPlanData = async () => {
        setLoading(true);
        try {
            const res = await getRequest(`/plan/get-plan-pricing/${id}`);

            if (res.status === 'success' && res.data) {
                const plan = res.data;

                // 1. Populate standard form fields
                setFormData({
                    plan_title: plan.plan_title || "",
                    plan_subtext: plan.plan_subtext || "",
                    plan_description: plan.plan_description || "",
                    plan_mrp: plan.plan_mrp || "",
                    plan_selling_price: plan.plan_selling_price || "",
                    plan_discount: plan.plan_discount || "",
                    plan_quarter_discount: plan.plan_quarter_discount || "",
                    plan_year_discount: plan.plan_year_discount || "",
                    plan_trial_days: plan.plan_trial_days || "",
                    plan_label: plan.plan_label || ""
                });

                // 2. Populate dynamic rows directly as arrays of objects
                if (plan.plan_includes && Array.isArray(plan.plan_includes) && plan.plan_includes.length > 0) {
                    setInclusions(plan.plan_includes);
                }

                if (plan.plan_key_features && Array.isArray(plan.plan_key_features) && plan.plan_key_features.length > 0) {
                    setFeatures(plan.plan_key_features);
                }
            }
        } catch (error) {
            console.error("Error fetching plan:", error);
            showToast.error("Failed to load plan data.");
        } finally {
            setLoading(false);
        }
    };

    // --- FORM HANDLERS ---
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // --- DYNAMIC LIST HANDLERS ---
    const handleDynamicChange = (index: number, field: string, value: string | boolean, type: 'inclusion' | 'feature') => {
        const list = type === 'inclusion' ? [...inclusions] : [...features];
        (list[index] as any)[field] = value;
        type === 'inclusion' ? setInclusions(list) : setFeatures(list);
    };

    const addDynamicRow = (type: 'inclusion' | 'feature') => {
        const list = type === 'inclusion' ? [...inclusions] : [...features];

        // Add the missing 'order' property here
        list.push({
            name: "",
            value: "",
            included: true,
            order: list.length // Sets the order based on the new position
        });

        type === 'inclusion' ? setInclusions(list) : setFeatures(list);
    };

    const removeDynamicRow = (index: number, type: 'inclusion' | 'feature') => {
        const list = type === 'inclusion' ? [...inclusions] : [...features];
        list.splice(index, 1);
        type === 'inclusion' ? setInclusions(list) : setFeatures(list);
    };

    // --- API SUBMISSION ---
    const handlePublish = async () => {
        if (!formData.plan_title || !formData.plan_selling_price) {
            showToast.error("Plan title and selling price are required.");
            return;
        }

        // Filter out any rows where the user left the "Name" blank
        const formattedInclusions = inclusions.filter(item => item.name.trim() !== "");
        const formattedFeatures = features.filter(item => item.name.trim() !== "");

        const payload = {
            plan_id: isEditMode ? id : undefined,
            ...formData,
            plan_mrp: Number(formData.plan_mrp) || 0,
            plan_selling_price: Number(formData.plan_mrp) || 0,
            plan_discount: Number(formData.plan_discount) || 0,
            plan_year_discount: Number(formData.plan_year_discount) || 0,
            plan_trial_days: Number(formData.plan_trial_days) || 0,
            plan_includes: formattedInclusions,      // Now passes the array of objects directly
            plan_key_features: formattedFeatures     // Now passes the array of objects directly
        };

        setLoading(true);
        try {
            const res = await postRequest('/plan/add-plan-pricing', payload);
            if (res.status === 'success') {
                showToast.success(res.message);
                navigate('/plans-pricing');
            } else {
                showToast.error(res.message);
            }
        } catch (error: any) {
            showToast.error(error.message || "An error occurred.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="hk-pg-wrapper">
            <div className="hk-pg-body py-0">
                <div className="page-wrap">
                    <div className="page-content ps-0">
                        <div className="page-detail-wrap">
                            <header className="page-header">
                                <div>
                                    <div className="page-title">
                                        <h2 className="mb-0">{isEditMode ? 'Update Plan' : 'Add Plan'}</h2>
                                    </div>
                                </div>
                                <div className="page-options-wrap">
                                    <div className="v-separator d-lg-inline-block d-none"></div>
                                    <a className="btn btn-icon btn-flush-dark btn-rounded flush-soft-hover hk-navbar-togglable d-lg-inline-block d-none"
                                        href="#" data-bs-toggle="tooltip" data-bs-placement="top" title=""
                                        data-bs-original-title="Collapse">
                                        <span className="btn-icon-wrap">
                                            <span className="feather-icon"><i className="ri-arrow-up-s-line ri-lg"></i></span>
                                            <span className="feather-icon d-none"><i className="ri-arrow-down-s-line ri-lg"></i></span>
                                        </span>
                                    </a>
                                </div>
                            </header>
                            <div className="page-body">
                                <div data-simplebar className="nicescroll-bar">
                                    <div className="container-fluid">
                                        <div className="row g-3">
                                            <div className="col-lg-8">

                                                {/* --- PLAN DETAILS --- */}
                                                <div className="card">
                                                    <div className="card-header">
                                                        <h6>Plans Details</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row g-3">
                                                            <div className="col-lg-12">
                                                                <div className="form-group mb-0">
                                                                    <label>Plan Name</label>
                                                                    <input type="text" name="plan_title" value={formData.plan_title} onChange={handleChange} className="form-control" />
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-12">
                                                                <div className="form-group">
                                                                    <label>Description</label>
                                                                    <textarea name="plan_description" value={formData.plan_description} onChange={handleChange} className="form-control"></textarea>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* --- PRICING DETAILS --- */}
                                                <div className="card">
                                                    <div className="card-header">
                                                        <h6>Pricing Details</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row g-3">
                                                            <div className="col-lg-12">
                                                                <div className="form-group mb-0">
                                                                    <label>Plan MRP (₹)</label>
                                                                    <input type="number" name="plan_mrp" value={formData.plan_mrp} onChange={handleChange} className="form-control" />
                                                                </div>
                                                            </div>


                                                            <div className="col-lg-4">
                                                                <div className="form-group mb-0">
                                                                    <label>Monthly Discount (₹)</label>
                                                                    <input type="number" name="plan_discount" value={formData.plan_discount} onChange={handleChange} className="form-control" />
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-4">
                                                                <div className="form-group mb-0">
                                                                    <label>Quarterly Discount (₹)</label>
                                                                    <input type="number" name="plan_quarter_discount" value={formData.plan_quarter_discount} onChange={handleChange} className="form-control" />
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-4">
                                                                <div className="form-group mb-0">
                                                                    <label>Yearly Discount (₹)</label>
                                                                    <input type="number" name="plan_year_discount" value={formData.plan_year_discount} onChange={handleChange} className="form-control" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* --- INCLUSIONS --- */}
                                                <div className="card">
                                                    <div className="card-header">
                                                        <h6>Inclusions</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        {inclusions.map((item, index) => (
                                                            <div className="row g-3 align-items-center mb-3" key={`inc-${index}`}>
                                                                {/* REORDER BUTTONS */}
                                                                <div className="col-auto d-flex flex-column">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-xs btn-link p-0 text-primary"
                                                                        onClick={() => moveRow(index, 'up', 'inclusion')}
                                                                        disabled={index === 0}
                                                                    >
                                                                        <i className="ri-arrow-up-s-line fs-14"></i>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-xs btn-link p-0 text-primary"
                                                                        onClick={() => moveRow(index, 'down', 'inclusion')}
                                                                        disabled={index === inclusions.length - 1}
                                                                    >
                                                                        <i className="ri-arrow-down-s-line fs-14"></i>
                                                                    </button>
                                                                </div>
                                                                <div className="col-lg-4">
                                                                    <div className="form-group mb-0">
                                                                        <input type="text" className="form-control" placeholder="Name" value={item.name} onChange={(e) => handleDynamicChange(index, 'name', e.target.value, 'inclusion')} />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-4">
                                                                    <div className="form-group mb-0">
                                                                        <input type="text" className="form-control" placeholder="Value (Optional)" value={item.value} onChange={(e) => handleDynamicChange(index, 'value', e.target.value, 'inclusion')} />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-2">
                                                                    <div className="form-check mb-0">
                                                                        <input id={`inc_check_${index}`} className="form-check-input" type="checkbox" checked={item.included} onChange={(e) => handleDynamicChange(index, 'included', e.target.checked, 'inclusion')} />
                                                                        <label htmlFor={`inc_check_${index}`} className="form-check-label">Included</label>
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-1">
                                                                    {index === 0 ? (
                                                                        <button type="button" className="btn btn-primary btn-block" style={{ padding: '7px' }} onClick={() => addDynamicRow('inclusion')}><i className="ri-add-line" style={{ fontSize: '20px' }}></i></button>
                                                                    ) : (
                                                                        <button type="button" className="btn btn-danger btn-block" style={{ padding: '7px' }} onClick={() => removeDynamicRow(index, 'inclusion')}><i className="ri-delete-bin-line" style={{ fontSize: '20px' }}></i></button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* --- KEY FEATURES & HIGHLIGHTS --- */}
                                                <div className="card">
                                                    <div className="card-header">
                                                        <h6>Key Features & Highlights</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        {features.map((item, index) => (
                                                            <div className="row g-3 align-items-center mb-3" key={`feat-${index}`}>
                                                                {/* REORDER CONTROLS */}
                                                                <div className="col-auto d-flex flex-column" style={{ minWidth: '30px' }}>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-xs btn-link p-0 text-primary"
                                                                        onClick={() => moveRow(index, 'up', 'feature')}
                                                                        disabled={index === 0}
                                                                    >
                                                                        <i className="ri-arrow-up-s-line fs-14" ></i>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-xs btn-link p-0 text-primary"
                                                                        onClick={() => moveRow(index, 'down', 'feature')}
                                                                        disabled={index === features.length - 1}
                                                                    >
                                                                        <i className="ri-arrow-down-s-line fs-14"></i>
                                                                    </button>
                                                                </div>
                                                                <div className="col-lg-4">
                                                                    <div className="form-group mb-0">
                                                                        <input type="text" className="form-control" placeholder="Feature" value={item.name} onChange={(e) => handleDynamicChange(index, 'name', e.target.value, 'feature')} />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-4">
                                                                    <div className="form-group mb-0">
                                                                        <input type="text" className="form-control" placeholder="Value (Optional)" value={item.value} onChange={(e) => handleDynamicChange(index, 'value', e.target.value, 'feature')} />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-2">
                                                                    <div className="form-check mb-0">
                                                                        <input id={`feat_check_${index}`} className="form-check-input" type="checkbox" checked={item.included} onChange={(e) => handleDynamicChange(index, 'included', e.target.checked, 'feature')} />
                                                                        <label htmlFor={`feat_check_${index}`} className="form-check-label">Included</label>
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-1">
                                                                    {index === 0 ? (
                                                                        <button type="button" className="btn btn-primary btn-block" style={{ padding: '7px' }} onClick={() => addDynamicRow('feature')}><i className="ri-add-line" style={{ fontSize: '20px' }}></i></button>
                                                                    ) : (
                                                                        <button type="button" className="btn btn-danger btn-block" style={{ padding: '7px' }} onClick={() => removeDynamicRow(index, 'feature')}><i className="ri-delete-bin-line" style={{ fontSize: '20px' }}></i></button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                            </div>

                                            <div className="col-lg-4">
                                                {/* --- PUBLISH --- */}
                                                <div className="card">
                                                    <div className="card-header">
                                                        <h6>Publish</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <p className="fs-7 text-muted">Clicking publish will make this plan available.</p>
                                                    </div>
                                                    <div className="card-footer">
                                                        <button type="button" className="btn btn-primary" onClick={handlePublish} disabled={loading}>
                                                            {loading ? 'Publishing...' : 'Publish'}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* --- PLAN LABEL --- */}
                                                <div className="card">
                                                    <div className="card-header">
                                                        <h6>Plan Label</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row g-3">
                                                            <div className="col-lg-12">
                                                                <div className="form-group mb-0">
                                                                    <input type="text" name="plan_label" value={formData.plan_label} onChange={handleChange} className="form-control" placeholder="Ex. Popular" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddPlan;